/**
 * Dev-only testing aid (gated by settings.dev_relativize_brand_urls).
 *
 * Metaobject URL fields accept only absolute URLs, so links sourced from them as
 * dynamic sources point at the production brand domains and can't be exercised on
 * a dev store. This rewrites those absolute brand links per environment:
 *   - on a brand-N.xyz dev host: swap the production brand domain for its matching
 *     brand-N.xyz domain (keeps cross-brand navigation working on dev);
 *   - on the shared *.myshopify.com host: relativize to the current path (no
 *     per-brand public domain there).
 *
 * Self-guarded to dev hosts so it can never rewrite a real link on production
 * even if left on. The prod<->dev map mirrors snippets/brand-domain.liquid.
 *
 * Header mega menu links are morphed in via section hydration (requestIdleCallback)
 * after first paint — href updates happen as attribute mutations, not new nodes.
 */
(() => {
  const onXyz = /\.xyz$/.test(location.hostname);
  const onMyshopify = /\.myshopify\.com$/.test(location.hostname);
  if (!onXyz && !onMyshopify) return;

  /** Production brand host -> matching brand-N.xyz dev host. */
  const DEV_HOST_BY_BRAND_HOST = {
    'hallwines.com': 'brand-1.xyz',
    'www.hallwines.com': 'brand-1.xyz',
    'waltwines.com': 'brand-2.xyz',
    'www.waltwines.com': 'brand-2.xyz',
    'bacawines.com': 'brand-3.xyz',
    'www.bacawines.com': 'brand-3.xyz',
    'michelfochwines.com': 'brand-4.xyz',
    'www.michelfochwines.com': 'brand-4.xyz',
  };

  /** @returns {string|null} rewritten href when it targets a brand host, else null */
  function rewrite(href) {
    if (!href) return null;
    try {
      const url = new URL(href, location.origin);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      const devHost = DEV_HOST_BY_BRAND_HOST[url.hostname];
      if (!devHost) return null;
      if (onXyz) {
        url.protocol = 'https:';
        url.hostname = devHost;
        return url.href;
      }
      // *.myshopify.com — no per-brand domain, so resolve on the current host.
      return url.pathname + url.search + url.hash;
    } catch (e) {
      // Not an absolute URL — leave it alone.
    }
    return null;
  }

  function patchAnchor(anchor) {
    const next = rewrite(anchor.getAttribute('href'));
    if (next !== null) anchor.setAttribute('href', next);
  }

  function patch(root) {
    root.querySelectorAll('a[href]').forEach(patchAnchor);
  }

  function run() {
    patch(document);

    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Hydration/morph updates existing anchors in place (mega menu, etc.).
        if (mutation.type === 'attributes' && mutation.attributeName === 'href') {
          if (mutation.target instanceof HTMLAnchorElement) patchAnchor(mutation.target);
          continue;
        }

        if (mutation.type !== 'childList') continue;

        // Original childList behavior — patch each added subtree immediately.
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('a[href]')) patchAnchor(node);
          if (node.querySelectorAll) patch(node);
        }
      }
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    });

    // Header section hydrates keyed targets (header-menu mega menus) after idle.
    const headerSection = document.querySelector('.header-section');
    if (headerSection instanceof HTMLElement) {
      new MutationObserver(() => {
        if (headerSection.dataset.hydrated === 'true') patch(headerSection);
      }).observe(headerSection, {
        attributes: true,
        attributeFilter: ['data-hydrated'],
      });
    }
  }

  window.__hallwinesRelativizeBrandUrls = () => patch(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
